import { Phone, Shield, Clock, Award, MapPin, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { organizationSchema, serviceSchema, websiteSchema, faqSchema, homeFaqs, seoKeywords } from '../utils/seoData';

const Home = () => {
  const services = [
    {
      icon: Shield,
      title: 'Emergency Snake Rescue',
      description: 'Fast, safe, and professional snake removal from your home or property.',
      link: '/snake-rescue',
    },
    {
      icon: Award,
      title: 'Snake Awareness Workshops',
      description: 'Educational programs for schools, communities, and organizations.',
      link: '/workshops',
    },
    {
      icon: CheckCircle,
      title: 'Safety Audits',
      description: 'Comprehensive safety assessments for apartments and corporates.',
      link: '/safety-audit',
    },
  ];

  const features = [
    'Certified & Experienced Professional',
    'Humane Snake Handling',
    'Quick Response Time',
    'All Types of Snakes',
    'Safety First Approach',
    'Post-Rescue Advice',
  ];

  const areas = [
    'Hebbal', 'Banaswadi', 'Kalyan Nagar', 'HBR Layout',
    'Marathahalli', 'Whitefield', 'KR Puram', 'Babusapalya',
    'Indiranagar', 'CV Raman Nagar', 'Hoodi', 'Mahadevapura'
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Snake Catcher Bangalore | 24/7 Snake Rescue Near Me - Call Now"
        description="24/7 snake catcher near you in Bangalore. Expert snake rescue & removal by Alhan & Mohan K. Call +91 8310755854 or +91 9845080903 now."
        keywords={seoKeywords.home}
        canonical="/"
        schemaData={[organizationSchema, serviceSchema, websiteSchema, faqSchema(homeFaqs)]}
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Clock className="inline h-4 w-4 mr-1" />
              24/7 Emergency Service Available
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              <span className="text-emerald-600">Snake Catcher Bangalore</span> — 24/7 Snake Rescue Near You
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8">
              Urgent snake removal in Bangalore by certified snake catchers Alhan Nawaz and Mohan K. Fast, safe, humane rescue from homes, bathrooms, kitchens and gardens across East & Central Bangalore.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="tel:+918310755854">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6">
                  <Phone className="mr-2 h-5 w-5" />
                  Alhan: +91 8310755854
                </Button>
              </a>
              <a href="tel:+919845080903">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6">
                  <Phone className="mr-2 h-5 w-5" />
                  Mohan: +91 9845080903
                </Button>
              </a>
            </div>
            <p className="text-sm text-gray-500 mt-4">Serving East & Central Bangalore - 24/7 Emergency Service</p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive snake safety solutions for residential and commercial properties
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow border-emerald-100">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-gray-900">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <Link to={service.link}>
                      <Button variant="link" className="text-emerald-600 hover:text-emerald-700 p-0">
                        Learn More →
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Why Choose Us?
                </h2>
                <p className="text-gray-600 mb-6">
                  With years of experience and hundreds of successful rescues, our team of expert snake catchers is your trusted resource for snake removal in Bangalore.
                </p>
                <ul className="space-y-3">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <a href="tel:+918310755854" className="inline-block mt-8">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Phone className="mr-2 h-5 w-5" />
                    Call for Immediate Help
                  </Button>
                </a>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Need Help Right Now?
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-emerald-50 rounded-lg">
                    <Phone className="h-8 w-8 text-emerald-600" />
                    <div>
                      <p className="text-sm text-gray-600">Call Alhan Nawaz</p>
                      <a href="tel:+918310755854" className="text-xl font-bold text-emerald-600">
                        +91 8310755854
                      </a>
                      <p className="text-xs text-gray-500 mt-1">Babusapalya - East Bangalore</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-emerald-50 rounded-lg">
                    <Phone className="h-8 w-8 text-emerald-600" />
                    <div>
                      <p className="text-sm text-gray-600">Call Mohan K</p>
                      <a href="tel:+919845080903" className="text-xl font-bold text-emerald-600">
                        +91 9845080903
                      </a>
                      <p className="text-xs text-gray-500 mt-1">Ulsoor - Central Bangalore</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <Clock className="h-8 w-8 text-gray-600" />
                    <div>
                      <p className="text-sm text-gray-600">Available</p>
                      <p className="text-lg font-semibold text-gray-900">24/7 Emergency Service</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rescue Gallery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Rescue Operations</h2>
              <p className="text-gray-600">Professional snake rescue services by expert handlers</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="overflow-hidden hover:shadow-xl transition-shadow">
                <img 
                  src="https://customer-assets.emergentagent.com/job_alhan-snakes/artifacts/0cc6b0o2_snake.png" 
                  alt="Cobra rescue by professional snake catcher in Bangalore"
                  className="w-full h-96 object-cover"
                />
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Cobra Rescue</h3>
                  <p className="text-gray-600">Venomous cobra safely rescued and relocated by our expert team</p>
                </CardContent>
              </Card>
              <Card className="overflow-hidden hover:shadow-xl transition-shadow">
                <img 
                  src="https://customer-assets.emergentagent.com/job_alhan-snakes/artifacts/orrlq0z4_snake2.png" 
                  alt="Rat snake rescue by professional snake catcher in Bangalore"
                  className="w-full h-96 object-cover"
                />
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Rat Snake Rescue</h3>
                  <p className="text-gray-600">Non-venomous rat snake safely handled and released in natural habitat</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Areas We Serve */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Areas We Serve</h2>
            <p className="text-gray-600 mb-8">
              Fast response across Bangalore East - 10km radius from Babusapalya
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {areas.map((area, index) => (
                <div key={index} className="bg-emerald-50 p-4 rounded-lg text-center">
                  <MapPin className="h-5 w-5 text-emerald-600 mx-auto mb-2" />
                  <p className="text-gray-800 font-medium">{area}</p>
                </div>
              ))}
            </div>
            <Link to="/areas" className="inline-block mt-8">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                View All Areas
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section - targets long-tail "snake catcher" queries */}
      <section className="py-16 bg-gray-50" data-testid="home-faq-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-600">Quick answers about snake catchers, rescue numbers and emergencies in Bangalore</p>
            </div>
            <div className="space-y-4">
              {homeFaqs.map((faq, idx) => (
                <details
                  key={idx}
                  className="group bg-white rounded-lg border border-emerald-100 p-5 shadow-sm open:shadow-md transition-shadow"
                  data-testid={`home-faq-item-${idx}`}
                >
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                    <h3 className="text-base md:text-lg font-semibold text-gray-900">{faq.question}</h3>
                    <span className="text-emerald-600 text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-gray-600 mt-3 text-sm md:text-base leading-relaxed">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-emerald-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Snake in Your Home? Don't Panic!
          </h2>
          <p className="text-xl mb-8 text-emerald-50">
            Call us now for immediate professional assistance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="tel:+918310755854">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Alhan: +91 8310755854
              </Button>
            </a>
            <a href="tel:+919845080903">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Mohan: +91 9845080903
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;